import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api";
import { useConversation } from "@elevenlabs/react";

const VoiceCtx = createContext(null);
const AGENT_ID = process.env.REACT_APP_ELEVENLABS_AGENT_ID;

// Map of intent.module -> app route
const MODULE_ROUTES = {
  calendar: "/calendar",
  messaging: "/messaging",
  geolocation: "/geolocation",
  hajj: "/hajj",
  murshid: "/murshid",
  guidance: "/murshid",
  profile: "/profile",
  settings: "/settings",
  terms: "/terms",
  home: "/dashboard",
  general: null,
};

const ASK_MODULES = new Set(["murshid", "guidance", "general"]);

// Keywords used to override intent classification when the user is on the
// Geolocation page. Fixes the bug where "find nearest mosque" typed on
// /geolocation would sometimes be routed to Ask Murshid.
const GEOLOCATION_KEYWORDS = [
  "mosque", "masjid", "halal", "restaurant", "directions", "navigate",
  "find", "nearest", "where is", "location", "map", "hospital", "shop",
  "take me", "how far", "postal code", "address", "nearby",
  "search for", "locate", "km", "miles", "distance", "route",
];
const MURSHID_KEYWORDS = [
  "halal ruling", "haram", "islamic ruling", "quran", "hadith", "fiqh",
  "dua", "madhab", "wudu", "prayer ruling", "is it permissible",
  "what does islam say", "sunnah ruling", "fatwa", "islamic question",
];

const _matches = (text, list) => {
  const t = (text || "").toLowerCase();
  return list.some((k) => t.includes(k));
};

// Crude UUID generator (no external dep)
function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return h.toString(36);
}

export function VoiceProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState("idle"); // idle | listening | speaking | error | processing
  const [isProcessing, setIsProcessing] = useState(false);
  // Unified transcript for pages that want to render live conversation
  // (e.g. Murshid). Accumulated from useConversation's onMessage callback.
  const [transcript, setTranscript] = useState([]);
  // Pages can hide the global BottomBar (Murshid renders its own inline input)
  const [hideBottomBar, setHideBottomBar] = useState(false);
  const sessionActive = useRef(false);
  const sessionId = useRef(uuid());
  const lastSentHashAt = useRef({ hash: "", ts: 0 });
  const debounceTimer = useRef(null);
  const responseQueue = useRef(0); // max depth 1

  const conversation = useConversation({
    onConnect: () => setState("listening"),
    onDisconnect: () => setState("idle"),
    onMessage: (msg) => {
      if (msg?.message) {
        // Accumulate both AI + user turns for the Murshid transcript.
        setTranscript((prev) => {
          const role = msg.source === "ai" ? "assistant" : "user";
          const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role,
            content: msg.message,
            timestamp: new Date().toISOString(),
            source: msg.source === "ai" ? "agent" : "voice",
          };
          // Dedup: same role + trimmed content within last 2s
          const last = prev[prev.length - 1];
          if (last && last.role === role && last.content.trim() === msg.message.trim()) return prev;
          return [...prev, entry];
        });
      }
      if (msg?.source === "ai" && msg?.message) {
        handleAgentText(msg.message);
      }
    },
    onError: (e) => {
      console.error("ElevenLabs error", e);
      setState("error");
      setIsProcessing(false);
      toast.error("Voice error. Please try again.");
      setTimeout(() => setState("idle"), 1500);
    },
    onModeChange: ({ mode }) => {
      if (mode === "speaking") setState("speaking");
      else if (mode === "listening") setState("listening");
    },
  });

  const handleAgentText = (text) => {
    // Existing pattern: [MODULE: name] — navigates.
    // Extended (backward-compatible): [MODULE: name : JSON] — navigates and
    // passes the JSON payload as router state.tag_data so pages like
    // Geolocation can render voice-driven results in-place.
    const m = /\[MODULE:\s*(\w+)(?:\s*:\s*(\{[\s\S]*?\}))?\s*\]/i.exec(text);
    if (m) {
      const target = MODULE_ROUTES[m[1].toLowerCase()];
      let tagData = null;
      if (m[2]) {
        try { tagData = JSON.parse(m[2]); } catch { /* ignore malformed */ }
      }
      if (target) {
        if (location.pathname !== target) {
          navigate(target, { state: { tag_data: tagData, ts: Date.now() } });
        } else if (tagData) {
          // Same-page update: replace so listeners react
          navigate(target, { state: { tag_data: tagData, ts: Date.now() }, replace: true });
        }
      }
    }
  };

  const toggleVoice = async () => {
    if (sessionActive.current) {
      try { await conversation.endSession(); } catch (err) { void err; }
      sessionActive.current = false;
      setState("idle");
      return;
    }
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      let dyn = {};
      let signedUrl = null;
      try {
        const r = await api.get("/voice/signed-url");
        dyn = r.data.dynamic_variables || {};
        signedUrl = r.data.signed_url;
      } catch (err) { void err; }

      // Read the Dnya JWT from localStorage so ElevenLabs Server Tools can
      // authenticate against protected /api/* endpoints. Token stays in
      // dynamicVariables only — never placed in URLs or logged.
      let userJwt = "";
      try {
        userJwt = localStorage.getItem("dnya_token") || "";
      } catch (err) { void err; }
      if (!userJwt) {
        console.warn("[VoiceProvider] No dnya_token in localStorage — starting ElevenLabs session without user_jwt. Protected Server Tools will fail until user signs in.");
      }

      const dynamicVariables = {
        ...dyn,
        session_id: sessionId.current,
        user_jwt: userJwt,
      };
      const opts = signedUrl
        ? { signedUrl, dynamicVariables }
        : { agentId: AGENT_ID, dynamicVariables };
      await conversation.startSession(opts);
      sessionActive.current = true;
    } catch (e) {
      console.error("voice start failed", e);
      toast.error("Could not start voice. Check microphone permission.");
      setState("error");
      setTimeout(() => setState("idle"), 1500);
    }
  };

  // Internal: do the actual text submission with locks + dedup + queue
  const doSend = useCallback(async (text) => {
    const v = text.trim();
    if (!v) return;
    if (isProcessing) {
      toast("Bismillah... still processing your last request");
      return;
    }
    // Dedup: same input within 3 seconds → ignore silently
    const h = hashString(v.toLowerCase());
    const now = Date.now();
    if (lastSentHashAt.current.hash === h && now - lastSentHashAt.current.ts < 3000) {
      return;
    }
    lastSentHashAt.current = { hash: h, ts: now };

    // Response queue depth check
    if (responseQueue.current >= 1) {
      toast("Bismillah... still processing");
      return;
    }
    responseQueue.current += 1;
    setIsProcessing(true);
    setState("processing");

    try {
      // If on Murshid, send DIRECTLY to murshid_ask (bypass intent classifier)
      if (location.pathname === "/murshid") {
        navigate("/murshid", { state: { autoAsk: v, sessionId: sessionId.current, ts: now }, replace: true });
      } else {
        const cur = location.pathname.split("/")[1] || "home";
        const r = await api.post("/voice/intent", {
          user_input: v,
          current_module: cur,
          session_id: sessionId.current,
        });
        const intent = r.data;
        if (intent.duplicate) return;
        if (intent.spoken_response) toast(intent.spoken_response);

        // ── Geolocation stickiness ────────────────────────────────────
        // If the user is on /geolocation and the query looks like a
        // location query (or is NOT clearly an Islamic-ruling question),
        // keep them on /geolocation regardless of intent classification.
        let effectiveModule = intent.module;
        if (cur === "geolocation") {
          const hasGeoWord = _matches(v, GEOLOCATION_KEYWORDS);
          const hasMurshidWord = _matches(v, MURSHID_KEYWORDS);
          if (hasGeoWord && !hasMurshidWord) {
            effectiveModule = "geolocation";
          } else if (
            ASK_MODULES.has(intent.module) && !hasMurshidWord
          ) {
            // Classifier said Murshid but the user is on the map and
            // didn't use any Murshid-y phrases → stay on geolocation.
            effectiveModule = "geolocation";
          }
        }

        if (effectiveModule && MODULE_ROUTES[effectiveModule] && effectiveModule !== "general" && effectiveModule !== "murshid" && effectiveModule !== "guidance") {
          navigate(MODULE_ROUTES[effectiveModule], { state: { intent: { ...intent, module: effectiveModule }, ts: now } });
        } else if (ASK_MODULES.has(effectiveModule)) {
          navigate("/murshid", { state: { autoAsk: v, intent, sessionId: sessionId.current, ts: now } });
        } else {
          navigate("/murshid", { state: { autoAsk: v, intent, sessionId: sessionId.current, ts: now } });
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not process your request");
    } finally {
      // Allow next request after a short cool-down
      setTimeout(() => {
        responseQueue.current = Math.max(0, responseQueue.current - 1);
        setIsProcessing(false);
        setState("idle");
      }, 350);
    }
  }, [isProcessing, location.pathname, navigate]);

  // Public: debounced text send (300ms)
  const sendText = useCallback((text) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => doSend(text), 300);
  }, [doSend]);

  // Public: immediate send (Enter key uses this)
  const sendTextImmediate = useCallback((text) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    doSend(text);
  }, [doSend]);

  // Text send via the SDK's sendUserMessage — surfaces to the agent as a user
  // turn without needing an active mic. If there is no active EL session yet,
  // startSession is called first so the agent can respond.
  const sendTextMessage = useCallback(async (text) => {
    const t = (text || "").trim();
    if (!t) return;
    // Locally record the user turn so the UI updates immediately
    setTranscript((prev) => [...prev, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      role: "user", content: t, timestamp: new Date().toISOString(), source: "text",
    }]);
    if (!sessionActive.current) {
      try { await toggleVoice(); } catch (err) { void err; }
    }
    try {
      if (conversation?.sendUserMessage) {
        await conversation.sendUserMessage(t);
      } else {
        console.warn("[VoiceProvider] sendUserMessage not available on this SDK version");
      }
    } catch (e) {
      console.error("[VoiceProvider] sendUserMessage failed", e);
    }
  }, [conversation]);

  const clearTranscript = useCallback(() => setTranscript([]), []);

  return (
    <VoiceCtx.Provider value={{
      state,
      isProcessing,
      toggleVoice,
      sendText,
      sendTextImmediate,
      sessionId: sessionId.current,
      // ── Additions for Murshid transcript sync ────────────────────────
      transcript,
      setTranscript,
      clearTranscript,
      sendTextMessage,
      conversationStatus: conversation?.status,
      isSpeaking: conversation?.isSpeaking || false,
      hideBottomBar,
      setHideBottomBar,
    }}>
      {children}
    </VoiceCtx.Provider>
  );
}

export const useVoice = () => useContext(VoiceCtx);
